import { default as styled } from '@emotion/styled'
import axios from 'axios'
import { Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Breakpoint from '../../Breakpoint'
import BadnetTournamentEvent from '../../components/global/BadnetTournamentEvent'
import Container from '../../components/global/Container'
import CustomCalendar from '../../components/global/CustomCalendar'
import Loader from '../../components/global/Loader'
import Title from '../../components/global/Title'
import { SERVER_URL } from '../../config'
import { useLangSelected } from '../../hooks/useLangSelected'
import { BadnetTournamentModel } from '../../types/badnetTournament.type'

const parseParticipantsData = (participants: string | null) => {
    if (!participants) return null

    const match = participants.match(/(\d+)\/(\d+|\?)/)
    if (!match) return null

    const current = parseInt(match[1], 10)
    const denominator = match[2]

    // Si le dénominateur est "?" (inconnu), on retourne seulement le nombre actuel
    if (denominator === '?') {
        return {
            currentParticipants: current,
            maxParticipants: undefined,
            fillPercentage: undefined,
            remainingPlaces: undefined,
        }
    }

    const max = parseInt(denominator, 10)
    const percentage = (current / max) * 100
    const remaining = max - current

    return {
        currentParticipants: current,
        maxParticipants: max,
        fillPercentage: percentage,
        remainingPlaces: remaining,
    }
}

const TileContent = ({ tournaments }: { tournaments: BadnetTournamentModel[] }) => {
    const truncateText = (text: string, maxLength: number) => {
        if (!text) return ''
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    }

    const cleanHtml = (html: string | null): string => {
        if (!html) return ''
        return html
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .trim()
    }

    return (
        <>
            <DesktopTileContent>
                {tournaments.slice(0, 3).map((tournament, index) => (
                    <TournamentItem key={tournament.badnetId}>
                        <TournamentIndicators>
                            {tournament.bvaMemberCount !== undefined && tournament.bvaMemberCount > 0 && (
                                <BvaMemberCountBadge title={`${tournament.bvaMemberCount} membre(s) BVA inscrit(s)`}>
                                    <StyledUsersIcon />
                                    <MemberCount>{tournament.bvaMemberCount}</MemberCount>
                                </BvaMemberCountBadge>
                            )}
                        </TournamentIndicators>
                        <TournamentInfo>
                            <TournamentName title={tournament.title}>
                                {truncateText(tournament.title, 16)}
                            </TournamentName>
                            <TournamentPlace title={cleanHtml(tournament.place)}>
                                {truncateText(cleanHtml(tournament.place), 10)}
                            </TournamentPlace>
                            {tournament.fillPercentage !== undefined && (
                                <DesktopProgressBar
                                    fillPercentage={tournament.fillPercentage}
                                    title={`${Math.round(tournament.fillPercentage)}% rempli`}
                                >
                                    <ProgressFill fillPercentage={tournament.fillPercentage} />
                                </DesktopProgressBar>
                            )}
                        </TournamentInfo>
                        {tournament.isComing && <UpcomingIndicator />}
                    </TournamentItem>
                ))}
                {tournaments.length > 3 && <MoreTournaments>+{tournaments.length - 3}</MoreTournaments>}
            </DesktopTileContent>
            <MobileTileContent>
                {tournaments.slice(0, 5).map((tournament, index) => (
                    <MobileTournamentItem key={tournament.badnetId}>
                        <TournamentDot isComing={tournament.isComing} />
                        {tournament.bvaMemberCount !== undefined && tournament.bvaMemberCount > 0 && (
                            <MobileMemberCountBadge title={`${tournament.bvaMemberCount} membre(s) BVA`}>
                                <StyledUsersIcon />
                                <MemberCount>{tournament.bvaMemberCount}</MemberCount>
                            </MobileMemberCountBadge>
                        )}
                    </MobileTournamentItem>
                ))}
                {tournaments.length > 5 && <MoreDots>...</MoreDots>}
            </MobileTileContent>
        </>
    )
}

const BadnetTournaments = () => {
    const [tournaments, setTournaments] = useState<BadnetTournamentModel[] | undefined>(undefined)
    const [selectedDate, setSelectedDate] = useState<Date | null>(null)
    const [showModal, setShowModal] = useState(false)

    const { t } = useTranslation('translation')

    const lng = useLangSelected()

    const loadTournaments = () =>
        axios
            .get(`${SERVER_URL}/badnet/stored`, {
                params: {
                    lang: lng,
                },
            })
            .then((res) => {
                const tournamentsWithParsedData = res.data.data.map((tournament: BadnetTournamentModel) => {
                    const participantsData = parseParticipantsData(tournament.participants)
                    return {
                        ...tournament,
                        ...participantsData,
                    }
                })
                setTournaments(tournamentsWithParsedData)
            })
            .catch((error) => {
                console.error('Error loading BadNet tournaments:', error)
                setTournaments([])
            })

    useEffect(() => {
        loadTournaments()
    }, [lng])

    // Helper function to format date as YYYY-MM-DD in local timezone
    const formatDateKey = (date: Date): string => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
    }

    // Group tournaments by date for calendar
    const getTournamentsByDate = () => {
        if (!tournaments) return {}
        const grouped: { [key: string]: BadnetTournamentModel[] } = {}

        tournaments.forEach((tournament) => {
            // Use parsedDate for calendar (ISO format), fallback to original date
            const dateStr = tournament.parsedDate || tournament.date

            // Skip tournaments with empty or invalid dates
            if (!dateStr || dateStr === '') {
                console.warn('Empty date for tournament:', tournament.badnetId)
                return
            }

            // Parse date as local time to avoid timezone offset issues
            const [year, month, day] = dateStr.split('-').map(Number)
            const date = new Date(year, month - 1, day)
            // Skip tournaments with invalid dates
            if (isNaN(date.getTime())) {
                console.warn('Invalid date for tournament:', tournament.badnetId, dateStr)
                return
            }
            const dateKey = dateStr

            if (!grouped[dateKey]) {
                grouped[dateKey] = []
            }
            grouped[dateKey].push(tournament)
        })

        return grouped
    }

    const tournamentsByDate = getTournamentsByDate()

    // Get dates with tournaments for calendar highlighting
    const getDatesWithTournaments = () => {
        return Object.keys(tournamentsByDate).map((dateStr) => {
            const [year, month, day] = dateStr.split('-').map(Number)
            return new Date(year, month - 1, day)
        })
    }

    const handleDateClick = (date: Date) => {
        const dateKey = formatDateKey(date)
        if (tournamentsByDate[dateKey] && tournamentsByDate[dateKey].length > 0) {
            setSelectedDate(date)
            setShowModal(true)
        }
    }

    const handleCalendarChange = (value: any) => {
        if (Array.isArray(value)) {
            setSelectedDate(value[0])
        } else if (value instanceof Date) {
            setSelectedDate(value)
        } else {
            setSelectedDate(null)
        }
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setSelectedDate(null)
    }

    // Custom tile content to show tournament info directly in calendar cells
    const tileContent = ({ date, view }: { date: Date; view: string }) => {
        if (view === 'month') {
            const dateKey = formatDateKey(date)
            const dayTournaments = tournamentsByDate[dateKey]

            if (dayTournaments && dayTournaments.length > 0) {
                return <TileContent tournaments={dayTournaments} />
            }
        }
        return null
    }

    // Custom tile class for styling
    const tileClassName = ({ date, view }: { date: Date; view: string }) => {
        if (view === 'month') {
            const dateKey = formatDateKey(date)
            if (tournamentsByDate[dateKey]) {
                return 'has-tournaments'
            }
        }
        return null
    }

    if (!tournaments) return <Loader />

    const selectedDateTournaments = selectedDate ? tournamentsByDate[formatDateKey(selectedDate)] || [] : []

    return (
        <Container>
            <Title dataTestId="Title">{t('page.badnetTournament.title')}</Title>

            <CalendarWrapper>
                <CalendarHeader>
                    <StyledCustomCalendar
                        value={selectedDate}
                        onChange={handleCalendarChange}
                        onClickDay={handleDateClick}
                        tileContent={tileContent}
                        tileClassName={tileClassName}
                        locale={lng}
                        defaultValue={new Date()}
                    />
                </CalendarHeader>
                <CalendarLegend>
                    <LegendItem>
                        <UpcomingIndicator />
                        <LegendText>{t('page.badnetTournament.upcoming')}</LegendText>
                    </LegendItem>
                    <LegendItem>
                        <BvaMemberCountBadge>1</BvaMemberCountBadge>
                        <LegendText>{t('page.badnetTournament.bvaMembersLegend')}</LegendText>
                    </LegendItem>
                </CalendarLegend>
            </CalendarWrapper>

            {showModal && (
                <ModalOverlay data-testid="modal-overlay" onClick={handleCloseModal}>
                    <ModalContent data-testid="modal-content" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>
                                {selectedDate?.toLocaleDateString(lng, {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                })}
                            </ModalTitle>
                            <CloseButton onClick={handleCloseModal}>{t('common.close')}</CloseButton>
                        </ModalHeader>
                        <ModalBody>
                            {selectedDateTournaments.length > 0 ? (
                                selectedDateTournaments.map((tournament: BadnetTournamentModel) => (
                                    <BadnetTournamentEvent key={tournament.badnetId} tournament={tournament} />
                                ))
                            ) : (
                                <NoData>{t('page.badnetTournament.no-tournaments-day')}</NoData>
                            )}
                        </ModalBody>
                    </ModalContent>
                </ModalOverlay>
            )}
        </Container>
    )
}

const CalendarWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    margin: 32px 0;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        margin: 16px 0;
        gap: 16px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        margin: 12px 0;
        gap: 12px;
    }
`

const CalendarHeader = styled.div`
    width: 100%;
    text-align: center;
`

const CalendarLegend = styled.div`
    display: flex;
    justify-content: center;
    gap: 24px;
    padding: 16px 0;
    flex-wrap: wrap;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        gap: 16px;
        padding: 12px 0;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        gap: 12px;
        padding: 8px 0;
    }
`

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 1.9rem;
    color: #666;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        font-size: 1.8rem;
        gap: 6px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        font-size: 1.75rem;
        gap: 4px;
    }
`

const LegendText = styled.span`
    font-weight: 500;
`

const StyledCustomCalendar = styled(CustomCalendar)`
    width: 100%;
`

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
    padding: 16px;
`

const ModalContent = styled.div`
    background: white;
    border-radius: 8px;
    max-width: 800px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        max-width: 95%;
        max-height: 95vh;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        max-width: 100%;
        max-height: 100vh;
        border-radius: 0;
    }
`

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px;
    border-bottom: 1px solid #ddd;
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        padding: 16px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        padding: 12px;
    }
`

const ModalTitle = styled.h2`
    font-size: 1.5rem;
    color: var(--primary-color);
    font-family: 'Roboto Semi Bold';
    margin: 0;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        font-size: 1.2rem;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        font-size: 1rem;
    }
`

const CloseButton = styled.button`
    background: none;
    border: none;
    color: #666;
    font-size: 1.2rem;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    transition: all 0.2s;

    &:hover {
        background-color: #f0f0f0;
        color: #333;
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        font-size: 1rem;
        padding: 6px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        font-size: 1.9rem;
        padding: 4px;
    }
`

const ModalBody = styled.div`
    padding: 24px;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        padding: 16px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        padding: 12px;
    }
`

const NoData = styled.div`
    text-align: center;
    padding: 32px;
    color: #666;
    font-size: 1.2rem;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        padding: 24px;
        font-size: 1rem;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        padding: 16px;
        font-size: 1.9rem;
    }
`

const TileContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 2px 0;
    width: 100%;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        gap: 1px;
        padding: 1px 0;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        gap: 1px;
        padding: 1px 0;
    }
`

const TournamentItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    font-size: 1.85rem;
    line-height: 1.3;

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        font-size: 1.75rem;
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        font-size: 1.7rem;
        gap: 2px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        font-size: 1.6rem;
        gap: 2px;
    }
`

const TournamentInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    flex: 1;
    min-width: 0;
`

const TournamentName = styled.span`
    color: var(--primary-color);
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 1.9rem;

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        font-size: 1.8rem;
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        font-size: 1.75rem;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        font-size: 1.65rem;
    }
`

const TournamentPlace = styled.span`
    color: #666;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 1.8rem;
    font-weight: 400;

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        font-size: 1.7rem;
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        font-size: 1.65rem;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        font-size: 1.55rem;
    }
`

const TournamentIndicators = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
    flex-shrink: 0;
`

const BvaMemberCountBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    background-color: #00a3e0;
    color: white;
    border-radius: 12px;
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1;
    box-shadow: 0 0 8px rgba(0, 163, 224, 0.5);
    animation: pulse 2s ease-in-out infinite;

    @keyframes pulse {
        0%,
        100% {
            transform: scale(1);
        }
        50% {
            transform: scale(1.15);
        }
    }

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        min-width: 22px;
        height: 22px;
        font-size: 1.7rem;
        padding: 0 5px;
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        min-width: 20px;
        height: 20px;
        font-size: 1.65rem;
        padding: 0 4px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        min-width: 18px;
        height: 18px;
        font-size: 1.55rem;
        padding: 0 3px;
    }
`

const StyledUsersIcon = styled(Users)`
    width: 16px;
    height: 16px;
    margin-right: 3px;

    @media (max-width: ${Breakpoint.LAPTOP_XS}) {
        width: 15px;
        height: 15px;
    }

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        width: 14px;
        height: 14px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        width: 12px;
        height: 12px;
        margin-right: 2px;
    }
`

const MemberCount = styled.span`
    font-size: 1.8rem;
    font-weight: 700;

    @media (max-width: ${Breakpoint.PHONE}) {
        font-size: 1.4rem;
    }
`

const UpcomingIndicator = styled.span`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: var(--primary-color);
    flex-shrink: 0;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        width: 8px;
        height: 8px;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        width: 6px;
        height: 6px;
    }
`

const MoreTournaments = styled.span`
    font-size: 1.6rem;
    color: #999;
    text-align: center;

    @media (max-width: ${Breakpoint.TABLET_XS}) {
        font-size: 1.5rem;
    }

    @media (max-width: ${Breakpoint.PHONE}) {
        font-size: 1.45rem;
    }
`

const DesktopTileContent = styled.div`
    @media (max-width: ${Breakpoint.PHONE}) {
        display: none;
    }
`

const MobileTileContent = styled.div`
    display: none;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px;
    justify-content: center;
    align-items: center;
    padding: 4px 0;

    @media (max-width: ${Breakpoint.PHONE}) {
        display: flex;
    }
`

const MobileTournamentItem = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
    position: relative;
`

const MobileMemberCountBadge = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 4px;
    background-color: #00a3e0;
    color: white;
    border-radius: 10px;
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1;
    box-shadow: 0 0 6px rgba(0, 163, 224, 0.5);
    animation: pulse 2s ease-in-out infinite;

    @keyframes pulse {
        0%,
        100% {
            transform: scale(1);
        }
        50% {
            transform: scale(1.15);
        }
    }
`

const DesktopProgressBar = styled.div<{ fillPercentage: number }>`
    width: 100%;
    height: 6px;
    background-color: #e0e0e0;
    border-radius: 3px;
    overflow: hidden;
    position: relative;
    margin-top: 2px;
`

const ProgressFill = styled.div<{ fillPercentage: number }>`
    height: 100%;
    width: ${(props) => props.fillPercentage}%;
    background-color: ${(props) => {
        if (props.fillPercentage >= 100) return '#8b0000'
        if (props.fillPercentage >= 75) return '#ff4444'
        if (props.fillPercentage >= 50) return '#ff8800'
        return '#00cc66'
    }};
    transition: width 0.3s ease;
`

const TournamentDot = styled.div<{ isComing: boolean }>`
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background-color: ${(props) => (props.isComing ? 'var(--primary-color)' : '#999')};
    flex-shrink: 0;

    @media (max-width: ${Breakpoint.PHONE}) {
        width: 12px;
        height: 12px;
    }
`

const MoreDots = styled.span`
    font-size: 1.6rem;
    color: #999;

    @media (max-width: ${Breakpoint.PHONE}) {
        font-size: 1.5rem;
    }
`

export default BadnetTournaments
